import { FC, ReactNode, useCallback, useRef, useState } from "react";
import { Link as NavigationLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Popover, { type PopoverActions } from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";

import type {
  AircraftOperationCompliance,
  AircraftOverviewColumn,
  AircraftOverviewRow,
} from "src/shared/api-types/certificate";

import type {
  ComplianceWithCoursesResponseHydrated as ComplianceWithCourses,
  CourseCertificate,
} from "src/api/compliance";
import Button from "src/components/Button";
import CertificateList from "src/components/Certificate/CertificateList";
import CourseStatusIcon from "src/components/CourseStatusIcon";
import ExpandableSection from "src/components/ExpandableSection";
import CertificateActions from "src/context/CertificateActions";
import CurrentUserCrewContext from "src/context/CurrentUserCrewContext";
import CourseHistory from "src/features/course-history";
import OperationDetailView from "src/features/compliance/shared/OperationDetailView";
import certificateStatusService from "src/services/certificateStatus";
import { formatDate } from "src/services/formatters";

export interface AircraftOverviewCell {
  anchor: HTMLElement;
  row: AircraftOverviewRow;
  column: AircraftOverviewColumn;
}

interface AircraftOverviewDetailsProps {
  crewId: number;
  cell: AircraftOverviewCell;
  // The member's compliance with courses: undefined while the crew's course data is still loading
  memberCompliance: ComplianceWithCourses | null | undefined;
  coursesFailed: boolean;
  onClose: () => void;
}

type StatusValue = AircraftOperationCompliance["operation"];

// CertificateList gives its actions column 12% of the table, too narrow in a popover for its 3 to 5 icons, so the
// table scrolled sideways: a fixed column fits three icons in a row, and more wrap onto a second line
const coursesSx = {
  mt: 2,
  "& thead th:last-of-type": { width: "10rem !important" },
  "& tbody td:last-of-type > div": { flexWrap: "wrap" },
};

const StatusTile: FC<{ label: string; value: StatusValue }> = ({ label, value }) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ backgroundColor: "#f5f5f5", borderRadius: 1, px: 1.5, py: 1, minWidth: 0 }}>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25 }}>
        <CourseStatusIcon status={value.status} />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {certificateStatusService.getByCode(value.status as any)?.getDisplayName(t) ?? "—"}
        </Typography>
        {value.validUntil && (
          <Typography variant="caption" color="textSecondary">
            {formatDate(value.validUntil)}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const SectionTitle: FC<{ status: StatusValue; children: ReactNode }> = ({ status, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <CourseStatusIcon status={status.status} />
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {children}
      </Typography>
    </Box>
    {status.validUntil && (
      <Typography variant="body2" sx={{ color: "#666", fontSize: "0.85em", ml: 2 }}>
        {formatDate(status.validUntil)}
      </Typography>
    )}
  </Box>
);

/**
 * The relation tree nodes behind one Aircraft Overview cell: the member's operation under its role and position,
 * and the aircraft-specific courses for the cell's aircraft type at that position and operation.
 */
const findCellNodes = (
  memberCompliance: ComplianceWithCourses,
  row: AircraftOverviewRow,
  column: AircraftOverviewColumn,
) => {
  const position = memberCompliance.relationTree.roles
    .find((role) => role.id === column.roleId)
    ?.positions.find((p) => p.id === column.positionId);
  const operation = position?.operations.find((o) => o.id === column.operationId);
  if (!position || !operation) return null;

  // The aircraft status combines the position's and the operation's aircraft courses for the type
  const aircraftCourseIds = Array.from(
    new Set([
      ...(position.aircraft?.find((aircraft) => aircraft.id === row.aircraftId)?.courseIds ?? []),
      ...(operation.aircraft?.find((aircraft) => aircraft.id === row.aircraftId)?.courseIds ?? []),
    ]),
  );
  return { position, operation, aircraftCourseIds };
};

const AircraftOverviewDetails: FC<AircraftOverviewDetailsProps> = ({
  crewId,
  cell,
  memberCompliance,
  coursesFailed,
  onClose,
}) => {
  const { t } = useTranslation();
  const popoverActions = useRef<PopoverActions>(null);
  const [operationExpanded, setOperationExpanded] = useState(true);
  const [aircraftExpanded, setAircraftExpanded] = useState(true);
  const [historyItem, setHistoryItem] = useState<CourseCertificate | null>(null);

  const resizeObserver = useRef<ResizeObserver | null>(null);

  // The content grows when the course data arrives and while a section expands (animated): reposition the popover
  // on every size change, so it stays inside the window. A callback ref, as the popover mounts its content later.
  const observeContent = useCallback((node: HTMLDivElement | null) => {
    resizeObserver.current?.disconnect();
    resizeObserver.current = null;
    if (!node || typeof ResizeObserver === "undefined") return;
    resizeObserver.current = new ResizeObserver(() => popoverActions.current?.updatePosition());
    resizeObserver.current.observe(node);
  }, []);

  const handleShowHistory = useCallback((certificate: CourseCertificate) => setHistoryItem(certificate), []);

  const { row, column } = cell;
  const compliance = row.operationCompliance[column.key];
  if (!compliance) return null;

  const limitations = compliance.limitations ?? [];
  const nodes = memberCompliance ? findCellNodes(memberCompliance, row, column) : null;
  const certificateMap = memberCompliance?.certificateMap ?? {};
  const operationCourseCount = (nodes?.operation.courseIds ?? []).filter((id) => certificateMap[id]).length;
  const aircraftCertificates = (nodes?.aircraftCourseIds ?? []).map((id) => certificateMap[id]).filter(Boolean);

  const renderCourses = () => {
    if (coursesFailed) {
      return (
        <Typography variant="body2" color="error">
          {t("aircraft_overview_details.courses_failed")}
        </Typography>
      );
    }
    if (memberCompliance === undefined) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="textSecondary">
            {t("aircraft_overview_details.loading_courses")}
          </Typography>
        </Box>
      );
    }
    if (!nodes) {
      return (
        <Typography variant="body2" color="textSecondary">
          {t("compliance.no_operation_courses")}
        </Typography>
      );
    }
    return (
      <>
        <ExpandableSection
          title={
            <SectionTitle status={compliance.operation}>
              {t("aircraft_overview_details.courses_title", {
                name: column.operationName,
                count: operationCourseCount,
                interpolation: { escapeValue: false },
              })}
            </SectionTitle>
          }
          expanded={operationExpanded}
          onChange={setOperationExpanded}
        >
          <Box sx={{ ml: 2 }}>
            <OperationDetailView
              operation={nodes.operation}
              position={nodes.position}
              certificateMap={certificateMap}
              showPositionCoursesCollapsed={true}
            />
          </Box>
        </ExpandableSection>
        <ExpandableSection
          title={
            <SectionTitle status={compliance.aircraft}>
              {t("aircraft_overview_details.courses_title", {
                name: row.aircraftName,
                count: aircraftCertificates.length,
                interpolation: { escapeValue: false },
              })}
            </SectionTitle>
          }
          expanded={aircraftExpanded}
          onChange={setAircraftExpanded}
        >
          <Box sx={{ ml: 2 }}>
            {aircraftCertificates.length > 0 ? (
              <CertificateList certificates={aircraftCertificates} variant="default" />
            ) : (
              <Typography variant="body2" color="textSecondary">
                {t("compliance.no_aircraft_courses")}
              </Typography>
            )}
          </Box>
        </ExpandableSection>
      </>
    );
  };

  return (
    <>
      <Popover
        action={popoverActions}
        open={Boolean(cell.anchor)}
        anchorEl={cell.anchor}
        onClose={onClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        marginThreshold={16}
        slotProps={{
          paper: {
            role: "dialog",
            "aria-label": `${row.userName} > ${column.operationName} · ${row.aircraftName}`,
            sx: {
              maxHeight: "calc(100vh - 32px)",
              width: "min(960px, 90vw)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            },
          },
        }}
      >
        <Box ref={observeContent} sx={{ overflowY: "auto", overflowX: "hidden", minHeight: 0, p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <CourseStatusIcon
              status={compliance.combined.status}
              underSupervision={compliance.underSupervision}
              hasLimitations={limitations.length > 0}
              showTooltip={false}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <NavigationLink
                to={`/crew/${crewId}/members/${row.userId}/compliance/aircraft#op-${column.operationId}-aircraft-${row.aircraftId}`}
              >
                <Typography variant="body1">{`${row.userName} > ${column.operationName} · ${row.aircraftName}`}</Typography>
              </NavigationLink>
              <Typography variant="body2" color="textSecondary">
                {`${column.roleName} › ${column.positionName}`}
                {!compliance.canPerform && ` · ${t("aircraft_overview_details.cannot_perform")}`}
              </Typography>
            </Box>
            <Button icon={<CloseIcon />} size={"small"} onClick={onClose} title={t("close")} />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.25, mt: 2 }}>
            <StatusTile
              label={`${t("aircraft_overview_details.operation")} · ${column.operationName}`}
              value={compliance.operation}
            />
            <StatusTile
              label={`${t("aircraft_overview_details.aircraft")} · ${row.aircraftName}`}
              value={compliance.aircraft}
            />
          </Box>

          {(compliance.underSupervision || limitations.length > 0) && (
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, mt: 1.5 }}>
              {compliance.underSupervision && (
                <Chip
                  label={t("aircraft_management.under_supervision")}
                  size="small"
                  sx={{ color: "#fff", backgroundColor: "#4254bc" }}
                />
              )}
              {limitations.length > 0 && (
                <>
                  <Typography variant="caption" color="textSecondary" sx={{ ml: compliance.underSupervision ? 1 : 0 }}>
                    {t("limitations.title")}
                  </Typography>
                  {limitations.map((limitation) => (
                    <Chip
                      key={limitation.definitionId}
                      label={limitation.displayValue}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </>
              )}
            </Box>
          )}

          <Box sx={coursesSx}>
            <CertificateActions.Provider value={{ showHistory: handleShowHistory, getBoughtCourses: () => [] }}>
              <CurrentUserCrewContext.Provider value={{ userId: row.userId, crewId }}>
                {renderCourses()}
              </CurrentUserCrewContext.Provider>
            </CertificateActions.Provider>
          </Box>
        </Box>
      </Popover>
      {historyItem && memberCompliance && (
        <CourseHistory
          userId={row.userId}
          certificate={historyItem}
          certificateMap={memberCompliance.certificateMap}
          onClose={() => setHistoryItem(null)}
          className="co-details-history"
        />
      )}
    </>
  );
};

export default AircraftOverviewDetails;
